import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InstrumentSelectionPage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    userData,
    refreshUser
  } = useAuth();
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedSkillLevel, setSelectedSkillLevel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    // If already completed onboarding, redirect to dashboard
    if (userData?.onboardingComplete) {
      navigate('/student-dashboard');
      return;
    }
    // Pre-fill if data exists
    if (userData?.instrument) {
      setSelectedInstrument(userData.instrument);
    }
    if (userData?.skillLevel) {
      setSelectedSkillLevel(userData.skillLevel);
    }
  }, [currentUser, userData, navigate]);
  const instruments = [{
    value: 'Piano',
    label: 'Piano',
    icon: '🎹'
  }, {
    value: 'Guitar',
    label: 'Guitar',
    icon: '🎸'
  }, {
    value: 'Violin',
    label: 'Violin',
    icon: '🎻'
  }, {
    value: 'Drums',
    label: 'Drums',
    icon: '🥁'
  }, {
    value: 'Voice',
    label: 'Voice',
    icon: '🎤'
  }, {
    value: 'Saxophone',
    label: 'Saxophone',
    icon: '🎷'
  }, {
    value: 'Trumpet',
    label: 'Trumpet',
    icon: '🎺'
  }, {
    value: 'Flute',
    label: 'Flute',
    icon: '🪈'
  }, {
    value: 'Cello',
    label: 'Cello',
    icon: '🎻'
  }, {
    value: 'Bass',
    label: 'Bass',
    icon: '🎸'
  }, {
    value: 'Other',
    label: 'Other',
    icon: '🎵'
  }];
  const skillLevels = [{
    value: 'Beginner',
    label: 'Beginner',
    description: 'Just starting out'
  }, {
    value: 'Intermediate',
    label: 'Intermediate',
    description: '1-3 years experience'
  }, {
    value: 'Advanced',
    label: 'Advanced',
    description: '3+ years experience'
  }, {
    value: 'Professional',
    label: 'Professional',
    description: 'Professional musician'
  }];
  const handleSubmit = async () => {
    if (!selectedInstrument || !selectedSkillLevel) {
      setError('Please select both an instrument and skill level');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }
      // Update Firestore
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        instrument: selectedInstrument,
        skillLevel: selectedSkillLevel,
        onboardingStep: 'preferences',
        updatedAt: new Date().toISOString()
      });
      // Refresh user data
      await refreshUser();
      // Navigate to preferences page (or skip to dashboard for now)
      navigate('/student-dashboard');
    } catch (error) {
      console.error('Error saving instrument selection:', error);
      setError('Failed to save your selection. Please try again.');
      setIsSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white font-sans" data-id="element-4813">
      <Navbar data-id="element-4814" />
      <main className="pt-20 pb-16" data-id="element-4815">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-4816">
          <div className="text-center mb-12" data-id="element-4817">
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-4818">
              Choose Your Instrument
            </h1>
            <p className="text-lg text-white/80" data-id="element-4819">
              Let's personalize your learning experience
            </p>
          </div>
          {error && <div className="mb-6 bg-red-500/20 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg" data-id="element-4820">
              {error}
            </div>}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-lg" data-id="element-4821">
            {/* Instrument Selection */}
            <div className="mb-8" data-id="element-4822">
              <h2 className="text-xl font-semibold text-white mb-4" data-id="element-4823">
                Select Your Primary Instrument
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-4824">
                {instruments.map(instrument => <button key={instrument.value} onClick={() => setSelectedInstrument(instrument.value)} className={`p-4 rounded-lg border-2 transition-all ${selectedInstrument === instrument.value ? 'border-green-500 bg-green-500/20' : 'border-white/20 bg-white/5 hover:border-white/40'}`} data-id="element-4825">
                    <div className="text-4xl mb-2" data-id="element-4826">{instrument.icon}</div>
                    <div className="text-sm font-medium text-white" data-id="element-4827">
                      {instrument.label}
                    </div>
                    {selectedInstrument === instrument.value && <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto mt-2" data-id="element-4828" />}
                  </button>)}
              </div>
            </div>
            {/* Skill Level Selection */}
            <div className="mb-8" data-id="element-4829">
              <h2 className="text-xl font-semibold text-white mb-4" data-id="element-4830">
                What's Your Skill Level?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4831">
                {skillLevels.map(level => <button key={level.value} onClick={() => setSelectedSkillLevel(level.value)} className={`p-4 rounded-lg border-2 transition-all text-left ${selectedSkillLevel === level.value ? 'border-green-500 bg-green-500/20' : 'border-white/20 bg-white/5 hover:border-white/40'}`} data-id="element-4832">
                    <div className="flex items-center justify-between" data-id="element-4833">
                      <div data-id="element-4834">
                        <div className="text-lg font-semibold text-white mb-1" data-id="element-4835">
                          {level.label}
                        </div>
                        <div className="text-sm text-white/70" data-id="element-4836">
                          {level.description}
                        </div>
                      </div>
                      {selectedSkillLevel === level.value && <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-4837" />}
                    </div>
                  </button>)}
              </div>
            </div>
            {/* Submit Button */}
            <button onClick={handleSubmit} disabled={!selectedInstrument || !selectedSkillLevel || isSubmitting} className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4838">
              {isSubmitting ? <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-4839">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4840"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4841"></path>
                  </svg>
                  Saving...
                </> : <>
                  Continue to Dashboard
                  <ArrowRightIcon className="h-5 w-5" data-id="element-4842" />
                </>}
            </button>
          </div>
        </div>
      </main>
      <Footer data-id="element-4843" />
    </div>;
};